<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-4"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-4"
  >
    <div
      v-if="visible"
      class="flex p-3 rounded-md shadow-md  focus-within:shadow-tinted transition-shadow w-full max-w-md mb-2"
      :class="typeClasses[type]"
      :data-test-id="generateDataTestId(dataTestId, 'snackbar')"
    >
      <!-- Icon based on type - "loading" shows a spinner instead, for actions still in progress -->
      <AM3Loading
        v-if="type === 'loading'"
        size="lg"
        color="brand"
        class="flex-shrink-0"
      />
      <AM3Icon
        v-else
        :type="iconMap[type]"
        size="lg"
        class="flex-shrink-0"
        :color="iconColor[type]"
      />

      <!-- Message content -->
      <div class="ml-3 flex-grow text-sm text-font-primary">
        <div class="capitalize font-bold mb-1.5">
          {{ title || defaultTitle[type] }}
        </div>
        <div v-html="message" />
      </div>

      <!-- Action slot -->
      <slot name="action" />

      <!-- Close button if dismissible -->
      <div>
        <button
          v-if="dismissible"
          class="ml-1 rounded-full outline outline-1 outline-transparent focus:outline-brand flex flex-col items-center justify-center hover:bg-brand-light transition-colors size-5"
          data-test-id="notification-close-button"
          :aria-label="`Dismiss notification -  ${title || defaultTitle}`"
          @click="$emit('close')"
        >
          <AM3Icon
            type="x-mark"
            size="sm"
            color="strong"
          />
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import AM3Loading from '@/components/am3-generic/base/AM3Loading.vue'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'

export type SnackbarType = 'success' | 'error' | 'warning' | 'info' | 'loading'

const props = defineProps({
  message: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    default: null,
  },
  type: {
    type: String as () => SnackbarType,
    default: 'info',
    validator: (value: string) => ['success', 'error', 'warning', 'info', 'loading'].includes(value),
  },
  visible: {
    type: Boolean,
    default: true,
  },
  dismissible: {
    type: Boolean,
    default: true,
  },
  dataTestId: {
    type: String,
    default: 'snackbar',
  },
})

const emit = defineEmits(['close'])

// Map types to icons (unused for "loading" - it renders AM3Loading instead, see template)
const iconMap = {
  success: 'check-circle',
  error: 'exclamation-circle',
  warning: 'exclamation-triangle',
  info: 'information-circle',
  loading: 'check-circle',
} as const

const iconColor = {
  success: 'success',
  error: 'danger',
  warning: 'warning',
  info: 'informative',
  loading: 'informative',
} as const

const defaultTitle = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Information',
  loading: 'In progress',
} as const

// Map types to style classes using Tailwind
const typeClasses = {
  success: 'bg-success-50 text-white',
  error: 'bg-error-50 text-white',
  warning: 'bg-warning-50 text-white',
  info: 'bg-info-50 text-white',
  loading: 'bg-surface-light',
} as const
</script>
